import PersonIcon from '@mui/icons-material/Person';
import { AppBar, Box, Button, Toolbar, useMediaQuery } from '@mui/material';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAnalytics } from '../hooks/useAnalytics';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { Container } from './atomic';
import { Logo, MobileDrawer, MobileMenuButton, NavigationButton } from './ui';

/**
 * @typedef {Object} HeaderProps
 * @property {() => void} scrollToCollective - Function to scroll to the collective section
 * @property {() => void} scrollToServices - Function to scroll to the services section
 * @property {() => void} scrollToContact - Function to scroll to the contact section
 */

/**
 * Header component that provides navigation functionality for the RSM Collective website.
 *
 * Features include:
 * - RSM Collective logo with click-to-top functionality
 * - Responsive navigation with mobile drawer menu
 * - Smooth scrolling to different page sections
 * - Mobile-friendly hamburger menu using Material-UI Drawer
 * - Clean, reusable component architecture
 *
 * @param {HeaderProps} props - Component props containing scroll functions
 * @returns {JSX.Element} Header component with navigation
 */
function Header({ scrollToCollective, scrollToServices }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width:680px)'); // 0-680px: mobile only
  const isSmallTablet = useMediaQuery(
    '(min-width:681px) and (max-width:959px)'
  ); // 681-959px: partial nav
  const isLargeTablet = useMediaQuery(
    '(min-width:960px) and (max-width:1199px)'
  ); // 960-1199px: more nav items
  const isTablet = useMediaQuery('(min-width:681px) and (max-width:1199px)'); // 681-1199px: any tablet
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const { blogInsightsEnabled, letsCollaborateEnabled } = useFeatureFlags();
  const { trackEvent, trackCTA } = useAnalytics();

  /**
   * Handles logo click - navigates home or scrolls to top
   */
  const handleLogoClick = () => {
    trackEvent('logo_click', {
      location: 'header',
      action: isHomePage ? 'scroll_to_top' : 'navigate_home',
      from_page: location.pathname,
    });

    if (isHomePage) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
    setMobileOpen(false);
  };

  /**
   * Handles navigation button clicks - scrolls on home page or navigates home then scrolls
   *
   * @param {() => void} scrollFn - The scroll function to execute
   * @param {string} hash - The hash to navigate to
   */
  const handleNavClick = (scrollFn, hash) => {
    if (isHomePage && scrollFn) {
      scrollFn();
    } else {
      navigate(`/${hash}`);
    }
    setMobileOpen(false);
  };

  /**
   * Navigation items configuration - dynamically includes items based on feature flags
   */
  const navigationItems = [
    {
      label: 'Our Community',
      onClick: () => {
        trackCTA('header_nav', 'Our Community', {
          destination: 'collective_section',
          from_page: location.pathname,
        });
        handleNavClick(scrollToCollective, '#collective');
      },
    },
    {
      label: 'Research & Stories',
      onClick: () => {
        trackCTA('header_nav', 'Research & Stories', {
          destination: '/updates',
          from_page: location.pathname,
        });
        navigate('/updates');
        setMobileOpen(false);
      },
    },
    {
      label: 'RSMC Services',
      onClick: () => {
        trackCTA('header_nav', 'RSMC Services', {
          destination: 'marketplace_section',
          from_page: location.pathname,
        });
        handleNavClick(scrollToServices, '#services');
      },
    },
    // Conditionally add Insights/Blog navigation
    ...(blogInsightsEnabled
      ? [
          {
            label: 'Insights',
            onClick: () => {
              trackCTA('header_nav', 'Insights', {
                destination: '/blog',
                from_page: location.pathname,
              });
              navigate('/blog');
            },
          },
        ]
      : []),
    // Conditionally add Let's Collaborate navigation
    ...(letsCollaborateEnabled
      ? [
          {
            label: "Let's Collaborate",
            onClick: () => {
              trackCTA('header_nav', "Let's Collaborate", {
                destination: '/lets-collaborate',
                from_page: location.pathname,
              });
              // Force page reload to avoid reCAPTCHA conflicts
              window.location.href = '/lets-collaborate';
            },
          },
        ]
      : []),
  ];

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: 'secondary.main',
          color: 'secondary.contrastText',
          borderBottom: '4px solid',
          borderBottomColor: 'primary.main',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar
            disableGutters
            sx={{
              justifyContent: 'space-between',
              minHeight: { xs: '72px', md: '84px' },
              px: 0,
              py: { xs: 1, md: 1.5 },
            }}
          >
            {/* Logo */}
            <Logo onClick={handleLogoClick} />

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box
                sx={{
                  display: 'flex',
                  gap: { md: 2, lg: 3 },
                  alignItems: 'center',
                }}
              >
                {navigationItems
                  .filter((item) => {
                    // At small tablet (681-959px): hide "Our Community" and "Research & Stories"
                    if (
                      isSmallTablet &&
                      (item.label === 'Our Community' ||
                        item.label === 'Research & Stories')
                    ) {
                      return false;
                    }
                    // At large tablet (960-1199px): hide only "Research & Stories"
                    if (isLargeTablet && item.label === 'Research & Stories') {
                      return false;
                    }
                    // At desktop (1200px+): show all items
                    return true;
                  })
                  .map((item) => (
                    <NavigationButton
                      key={item.label}
                      label={item.label}
                      onClick={item.onClick}
                    />
                  ))}

                {/* Tablet (681-1199px): Icon Sign In + Hamburger Menu */}
                {isTablet && (
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        trackCTA('header_nav', 'Sign In', {
                          destination: '/sign-in',
                          from_page: location.pathname,
                          device: isLargeTablet
                            ? 'large_tablet'
                            : 'small_tablet',
                        });
                        navigate('/sign-in');
                      }}
                      sx={{
                        color: 'secondary.contrastText',
                        borderColor: 'secondary.contrastText',
                        textTransform: 'none',
                        fontWeight: 500,
                        minWidth: 'auto',
                        px: 1.5,
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: '8px',
                        borderBottomLeftRadius: '8px',
                        borderBottomRightRadius: 0,
                        '&:hover': {
                          backgroundColor:
                            'color-mix(in srgb, var(--color-primary) 10%, transparent)',
                          borderColor: 'primary.main',
                          color: 'primary.main',
                        },
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 18 }} />
                    </Button>
                    <MobileMenuButton onClick={() => setMobileOpen(true)} />
                  </>
                )}

                {/* Desktop (1200px+): Full Sign In Button */}
                {!isTablet && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PersonIcon />}
                    onClick={() => {
                      trackCTA('header_nav', 'Sign In', {
                        destination: '/sign-in',
                        from_page: location.pathname,
                        device: 'desktop',
                      });
                      navigate('/sign-in');
                    }}
                    sx={{
                      color: 'secondary.contrastText',
                      borderColor: 'secondary.contrastText',
                      textTransform: 'none',
                      fontWeight: 500,
                      px: 1.5,
                      py: 0.5,
                      minHeight: 'auto',
                      borderTopLeftRadius: 0,
                      borderTopRightRadius: '12px',
                      borderBottomLeftRadius: '12px',
                      borderBottomRightRadius: 0,
                      '&:hover': {
                        backgroundColor:
                          'color-mix(in srgb, var(--color-primary) 10%, transparent)',
                        borderColor: 'primary.main',
                        color: 'primary.main',
                      },
                    }}
                  >
                    Sign In
                  </Button>
                )}
              </Box>
            )}

            {/* Mobile Actions (0-680px) */}
            {isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    trackCTA('header_nav', 'Sign In', {
                      destination: '/sign-in',
                      from_page: location.pathname,
                      device: 'mobile',
                    });
                    navigate('/sign-in');
                  }}
                  sx={{
                    color: 'secondary.contrastText',
                    borderColor: 'secondary.contrastText',
                    textTransform: 'none',
                    fontWeight: 500,
                    minWidth: 'auto',
                    px: 1.5,
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: '8px',
                    borderBottomLeftRadius: '8px',
                    borderBottomRightRadius: 0,
                    '&:hover': {
                      backgroundColor:
                        'color-mix(in srgb, var(--color-primary) 10%, transparent)',
                      borderColor: 'primary.main',
                      color: 'primary.main',
                    },
                  }}
                >
                  <PersonIcon sx={{ fontSize: 18 }} />
                </Button>
                <MobileMenuButton onClick={() => setMobileOpen(true)} />
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <MobileDrawer
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        navigationItems={[
          ...navigationItems,
          {
            label: 'Sign In',
            onClick: () => {
              trackCTA('mobile_drawer', 'Sign In', {
                destination: '/sign-in',
                from_page: location.pathname,
                device: 'mobile',
              });
              navigate('/sign-in');
              setMobileOpen(false);
            },
          },
        ]}
        onLogoClick={handleLogoClick}
      />
    </>
  );
}

export default Header;
